/**
 * Écran « Modules », réservé à l'administration : activer ou désactiver un
 * module, régler ses paramètres. Toute modification prend effet au prochain
 * chargement de l'application.
 */

import { horodatageFr } from '@scc/core';
import { LONGUEUR_MAX_TEXTE } from '@scc/modules';
import { adminModules, modifierModule, ErreurApi, type ModuleAdmin, type ReglageAdmin } from '../api.js';
import { h, vider } from '../dom.js';
import { enteteSection, noteLecture } from '../composants.js';

/** `rechargerNavigation` : le changement touche la navigation, qui ne se met à jour qu'au rechargement. */
type Signaler = (texte: string, ok: boolean, rechargerNavigation?: boolean) => void;

/** Redessine la liste puis rend le focus au contrôle `idFocus`. */
type Rafraichir = (idFocus: string) => Promise<void>;

function champReglage(
  m: ModuleAdmin,
  r: ReglageAdmin,
  signaler: Signaler,
  rafraichir: Rafraichir,
): HTMLElement {
  const d = r.definition;
  const id = `reglage-${m.id}-${r.cle}`;
  const enregistrer = (valeur: string | number | boolean | null): void => {
    void (async () => {
      try {
        await modifierModule(m.id, { reglages: { [r.cle]: valeur } });
        signaler(`Réglage « ${d.libelle} » enregistré.`, true);
      } catch (e) {
        signaler(e instanceof ErreurApi ? e.message : 'Enregistrement refusé.', false);
      }
      // Qu'il ait réussi ou non, la liste reprend l'état du serveur : un champ
      // refusé ne doit pas garder une valeur qui n'est pas enregistrée.
      await rafraichir(id);
    })();
  };

  let controle: HTMLElement;
  if (d.type === 'booleen') {
    const c = h('input', { type: 'checkbox', id });
    c.checked = r.valeur === true;
    c.addEventListener('change', () => {
      enregistrer(c.checked);
    });
    controle = c;
  } else if (d.type === 'choix') {
    const s = h(
      'select',
      { id },
      ...Object.entries(d.options).map(([v, l]) => h('option', { value: v }, l)),
    );
    s.value = String(r.valeur);
    s.addEventListener('change', () => {
      enregistrer(s.value);
    });
    controle = s;
  } else if (d.type === 'nombre') {
    const i = h('input', {
      type: 'number',
      id,
      step: 'any',
      min: d.min ?? null,
      max: d.max ?? null,
      placeholder: String(d.defaut),
    });
    i.value = String(r.valeur);
    i.addEventListener('change', () => {
      // Champ vidé : la surcharge est retirée et la valeur par défaut reprend la main.
      enregistrer(i.value === '' ? null : Number(i.value));
    });
    controle = i;
  } else {
    const i = h('input', { type: 'text', id, maxlength: LONGUEUR_MAX_TEXTE, placeholder: d.defaut });
    i.value = String(r.valeur);
    i.addEventListener('change', () => {
      enregistrer(i.value === '' ? null : i.value);
    });
    controle = i;
  }

  return h(
    'div',
    { classe: 'reglage-module' },
    h('label', { for: id }, d.libelle, d.type === 'nombre' && d.unite ? ` (${d.unite})` : ''),
    controle,
    d.aide ? h('p', { classe: 'note-lecture' }, d.aide) : null,
  );
}

export function vueModules(): HTMLElement {
  const corps = h('div', {}, h('p', { classe: 'note-lecture' }, 'Chargement des modules…'));
  const retour = h('div', { role: 'status' });

  const signaler: Signaler = (texte, ok, rechargerNavigation = false) => {
    vider(retour);
    retour.appendChild(
      h('p', { classe: `retour-editeur ${ok ? 'ok' : 'ko'}`, role: ok ? null : 'alert' }, texte),
    );
    if (ok && rechargerNavigation) {
      retour.appendChild(
        h('p', { classe: 'note-lecture' }, 'Rechargez la page pour voir la navigation à jour.'),
      );
    }
  };

  const charger = async (idFocus?: string): Promise<void> => {
    try {
      const { modules } = await adminModules();
      const nomDe = (id: string): string => modules.find((x) => x.id === id)?.nom ?? id;
      vider(corps);
      for (const m of modules) {
        const interrupteur = h('input', {
          type: 'checkbox',
          id: `module-${m.id}`,
          disabled: !m.desactivable,
        });
        interrupteur.checked = m.actifDemande;
        interrupteur.addEventListener('change', () => {
          void (async () => {
            // Verrouillé le temps de l'appel : deux bascules rapprochées
            // s'écraseraient dans un ordre indéterminé.
            interrupteur.disabled = true;
            try {
              await modifierModule(m.id, { actif: interrupteur.checked });
              signaler(
                `Module « ${m.nom} » ${interrupteur.checked ? 'activé' : 'désactivé'}.`,
                true,
                true,
              );
            } catch (e) {
              interrupteur.checked = !interrupteur.checked;
              signaler(e instanceof ErreurApi ? e.message : 'Modification refusée.', false);
            }
            await charger(`module-${m.id}`);
          })();
        });

        const bloquantes = m.dependances.filter((id) => modules.find((x) => x.id === id)?.actif !== true);

        corps.appendChild(
          h(
            'div',
            { classe: 'card carte-module' },
            h(
              'div',
              { classe: 'entete-module' },
              interrupteur,
              h('label', { for: `module-${m.id}` }, h('strong', {}, m.nom)),
              h(
                'span',
                { classe: `pastille-etat ${m.actif ? 'actif' : 'inactif'}` },
                m.actif ? 'Actif' : 'Inactif',
              ),
            ),
            h('p', {}, m.description),
            !m.desactivable ? h('p', { classe: 'note-lecture' }, 'Module du noyau : toujours actif.') : null,
            m.actifDemande && !m.actif && bloquantes.length > 0
              ? h(
                  'p',
                  { classe: 'note-lecture' },
                  `Inactif car une dépendance est désactivée : ${bloquantes.map(nomDe).join(', ')}.`,
                )
              : null,
            m.dependances.length > 0
              ? h('p', { classe: 'note-lecture' }, `Dépend de : ${m.dependances.map(nomDe).join(', ')}.`)
              : null,
            m.dependants.length > 0
              ? h('p', { classe: 'note-lecture' }, `Requis par : ${m.dependants.map(nomDe).join(', ')}.`)
              : null,
            ...m.reglages.map((r) => champReglage(m, r, signaler, charger)),
            m.modifieLe
              ? h(
                  'p',
                  { classe: 'note-lecture' },
                  `Modifié le ${horodatageFr(m.modifieLe)} par ${m.modifiePar ?? '—'}.`,
                )
              : null,
          ),
        );
      }
      if (idFocus) document.getElementById(idFocus)?.focus();
    } catch {
      vider(corps);
      corps.appendChild(
        h('div', { classe: 'erreur-bloc' }, 'La liste des modules n’a pas pu être chargée.'),
      );
    }
  };
  void charger();

  return h(
    'div',
    { classe: 'page', id: 'page-modules', role: 'region', 'aria-label': 'Modules' },
    h(
      'section',
      {},
      enteteSection(1, 'Modules', 'Activer, désactiver et régler les modules de l’application'),
      noteLecture(
        'Un module désactivé disparaît de la navigation et ses données ne sont plus servies. ' +
          'Le noyau ne se désactive pas ; un module requis par un autre module actif non plus.',
      ),
      retour,
      corps,
    ),
  );
}
